import { useEffect, useRef, useState } from "react";
import {
  useConfiguration,
  useUpdateUserConfiguration,
} from "../../../../hooks/useConfiguration";

/** Мери дали съдържанието на реф-а е по-високо от `threshold` px (ResizeObserver). */
function useRowOverflow(threshold: number, deps: unknown[]) {
  const [rowOverflows, setRowOverflows] = useState(false);
  const rowClipRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const clipEl = rowClipRef.current;
    if (!clipEl) return;
    const measure = () => setRowOverflows(clipEl.scrollHeight > threshold);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(clipEl);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return { rowClipRef, rowOverflows };
}

/** Един ред chips е 40px с бордърите, втори ред започва след gap-а (4px) на 44px */
export const ONE_CHIP_ROW = 44;
/** Два реда chips (2×40 + 4 gap) — прагът на тесния панел вдясно */
export const TWO_CHIP_ROWS = 88;

/** Коя лична настройка държи разгъването: ГОРНИЯТ филтър (над каталога) или
 * ДЕСНИЯТ (над прегледа). Двете са независими; имената са по мястото на реда,
 * защото съдържанието му (групи или станции) зависи от `catalogFilter`. */
export type ChipRowsSetting = "topFilterExpanded" | "sideFilterExpanded";

/**
 * Свиване/разгъване на chips (групи ИЛИ станции) — общо за реда над каталога
 * (`topFilterExpanded`) и за секцията вдясно над прегледа (`sideFilterExpanded`).
 * Състоянието е лична настройка в org базата и следва потребителя: кликът на
 * ъгълчето я записва директно (оптимистично, без localStorage) и при следващо
 * влизане се помни. Бутонът се показва само когато chips-овете прелеят над
 * `overflowThreshold` (ResizeObserver).
 */
export function useChipRows(
  setting: ChipRowsSetting,
  overflowThreshold: number,
  deps: unknown[],
) {
  const { configuration } = useConfiguration();
  const { mutate: updateUserConfiguration } = useUpdateUserConfiguration();
  const showAllRows = configuration[setting];
  const toggleShowAllRows = () =>
    updateUserConfiguration({ [setting]: !showAllRows });

  const { rowClipRef, rowOverflows } = useRowOverflow(overflowThreshold, deps);

  return { rowClipRef, rowOverflows, showAllRows, toggleShowAllRows };
}
